import * as React from "react";
import { cn } from "../../lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

/**
 * Truncates long text visually; full value remains for search/filters.
 * Hover shows the full name in a tooltip when text is clipped.
 * Pass lines={0} (or null/false) to show the full wrapped content.
 */
export function TruncatedText({
  value,
  className,
  as: Comp = "span",
  href,
  lines = 1,
  alwaysTooltip = false,
}) {
  if (value == null || value === "") {
    return <span className="text-muted-foreground">-</span>;
  }

  const text = String(value);
  const clampLines = typeof lines === "number" && lines > 0 ? lines : 0;
  const isClipped = clampLines > 0;
  const showTooltip = alwaysTooltip || (isClipped && text.length > 40);

  const contentClass = cn(
    !isClipped
      ? "block break-words whitespace-normal"
      : clampLines === 1
        ? "block truncate"
        : "block overflow-hidden text-ellipsis break-words",
    className
  );

  const style =
    clampLines > 1
      ? {
          display: "-webkit-box",
          WebkitLineClamp: clampLines,
          WebkitBoxOrient: "vertical",
        }
      : undefined;

  const inner = href ? (
    <a
      href={href}
      rel="noreferrer"
      target="_blank"
      className={cn(contentClass, "text-blue-600 hover:underline")}
      style={style}
    >
      {text}
    </a>
  ) : (
    <Comp className={contentClass} style={style}>
      {text}
    </Comp>
  );

  if (!showTooltip) return inner;

  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="block min-w-0 max-w-full">{inner}</span>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="start"
          className="max-w-md break-words whitespace-normal"
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
